/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@mui/material';
import { Col, Row } from 'reactstrap';
import AvTextField from '../shared/components/AvTextField';
import { spacing } from '../shared/themes/appTheme';
import { isWhitespaceOnly } from '../shared/utils/validators';

function CompanyInfoQuestions({
  companyName, questions, setCompanyInfoAnswer, disabled,
}) {
  return (
    <>
      <h5 style={{ marginTop: spacing(3) }}>Company Information</h5>
      <AvTextField
        label="Company Name"
        InputProps={{ readOnly: true }}
        variant="standard"
        disabled
        value={companyName}
        fullWidth
      />
      {questions.map((question) => (
        <Autocomplete
          key={question.name}
          disabled={disabled}
          options={question.options}
          value={question.value}
          onChange={(e, value) => setCompanyInfoAnswer(question.name, value)}
          renderInput={(params) => (
            <AvTextField
              required
              {...params}
              name={question.name}
              label={question.label}
              variant="standard"
            />
          )}
        />
      ))}
    </>
  );
}

function InvestmentStatementQuestion({ question, setInvestmentStatementAnswer, disabled }) {
  const selectedAnswers = question.value || [];

  return (
    <>
      <FormLabel required style={{ color: 'rgb(81 84 97)', marginTop: spacing(2) }}>{question.label}</FormLabel>
      <FormGroup>
        {question.options.map((option) => (
          <FormControlLabel
            key={option}
            label={option}
            control={(
              <Checkbox
                inputProps={{ 'aria-label': option }}
                disabled={disabled}
                checked={selectedAnswers.includes(option)}
                onChange={() => setInvestmentStatementAnswer(option)}
              />
            )}
          />
        ))}
      </FormGroup>
    </>
  );
}

function BinaryQuestionList({ questions, setBinaryQuestionAnswer, disabled }) {
  return (
    questions.map((question) => (
      <Row className="mt-md-3" key={question.name || question.label}>
        <Col className="col-9">
          <FormLabel required style={{ color: 'rgb(81 84 97)' }}>{question.label}</FormLabel>
        </Col>
        <Col className="col-3">
          <FormGroup className="float-right" row>
            <FormControlLabel
              control={(
                <Checkbox
                  inputProps={{ 'aria-label': `${question.label} yes` }}
                  disabled={disabled}
                  checked={question.value === true}
                  onClick={() => setBinaryQuestionAnswer(
                    question.name || question.label,
                    { value: true },
                  )}
                />
              )}
              label="Yes"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  inputProps={{ 'aria-label': `${question.label} no` }}
                  disabled={disabled}
                  checked={question.value === false}
                  onClick={() => setBinaryQuestionAnswer(
                    question.name || question.label,
                    { value: false },
                  )}
                />
              )}
              label="No"
            />
          </FormGroup>
        </Col>
        {question.value === false && typeof question.exp === 'string' && (
          <Col className="col-12">
            <AvTextField
              fullWidth
              value={question.exp}
              onChange={(event) => {
                const text = event.target.value;
                if (!isWhitespaceOnly(text) && text.length <= 200) {
                  setBinaryQuestionAnswer(question.name || question.label, { exp: text });
                }
              }}
              label="Please provide an explanation"
              variant="standard"
              multiline
              required
              disabled={disabled}
            />
          </Col>
        )}
      </Row>
    ))
  );
}

export default function Questions({
  companyName,
  questionSections,
  setQuestionSections,
  disabled,
}) {
  const setCompanyInfoAnswer = (name, value) => {
    setQuestionSections((prev) => ({
      ...prev,
      companyInfo: prev.companyInfo.map((question) => {
        if (question.name === name) {
          return { ...question, value };
        }

        return question;
      }),
    }));
  };

  const setInvestmentStatementAnswer = (option) => {
    setQuestionSections((prev) => {
      const selectedAnswers = prev.investmentStatement.value || [];
      const value = selectedAnswers.includes(option)
        ? selectedAnswers.filter((answer) => answer !== option)
        : [...selectedAnswers, option];

      return {
        ...prev,
        investmentStatement: {
          ...prev.investmentStatement,
          value,
        },
      };
    });
  };

  const setBinaryQuestionAnswer = (questionKey, answer) => {
    setQuestionSections((prev) => {
      const updateQuestion = (question) => (
        (question.name || question.label) === questionKey ? { ...question, ...answer } : question
      );

      return {
        ...prev,
        digitalAssetQuestions: prev.digitalAssetQuestions.map(updateQuestion),
        closingDocsReviewQuestions: prev.closingDocsReviewQuestions.map(updateQuestion),
        yesNoQuestions: prev.yesNoQuestions.map(updateQuestion),
      };
    });
  };

  return (
    <>
      <CompanyInfoQuestions
        companyName={companyName}
        questions={questionSections.companyInfo}
        setCompanyInfoAnswer={setCompanyInfoAnswer}
        disabled={disabled}
      />
      <InvestmentStatementQuestion
        question={questionSections.investmentStatement}
        setInvestmentStatementAnswer={setInvestmentStatementAnswer}
        disabled={disabled}
      />
      <BinaryQuestionList
        questions={[
          ...questionSections.digitalAssetQuestions,
          ...questionSections.closingDocsReviewQuestions,
          ...questionSections.yesNoQuestions,
        ]}
        setBinaryQuestionAnswer={setBinaryQuestionAnswer}
        disabled={disabled}
      />
    </>
  );
}

const companyInfoQuestionShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
});

const investmentStatementQuestionShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
});

const binaryQuestionShape = PropTypes.shape({
  name: PropTypes.string,
  label: PropTypes.string.isRequired,
  exp: PropTypes.string,
  value: PropTypes.bool,
});

CompanyInfoQuestions.propTypes = {
  companyName: PropTypes.string.isRequired,
  questions: PropTypes.arrayOf(companyInfoQuestionShape).isRequired,
  setCompanyInfoAnswer: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

InvestmentStatementQuestion.propTypes = {
  question: investmentStatementQuestionShape.isRequired,
  setInvestmentStatementAnswer: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

BinaryQuestionList.propTypes = {
  questions: PropTypes.arrayOf(binaryQuestionShape).isRequired,
  setBinaryQuestionAnswer: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

Questions.propTypes = {
  companyName: PropTypes.string.isRequired,
  questionSections: PropTypes.shape({
    companyInfo: PropTypes.arrayOf(companyInfoQuestionShape).isRequired,
    investmentStatement: investmentStatementQuestionShape.isRequired,
    digitalAssetQuestions: PropTypes.arrayOf(binaryQuestionShape).isRequired,
    closingDocsReviewQuestions: PropTypes.arrayOf(binaryQuestionShape).isRequired,
    yesNoQuestions: PropTypes.arrayOf(binaryQuestionShape).isRequired,
  }).isRequired,
  setQuestionSections: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};
